import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const DiscoverContent: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Viewer Guide • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          How to Discover New Content on IndeeClips
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips has millions of videos across every category imaginable. Finding content you'll love doesn't have to be random—our platform is designed to help you discover exactly what you're looking for, plus introduce you to new creators you never knew you needed. This guide shows you every way to find great content on IndeeClips.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          1. The Homepage Feed: Your Personalized Recommendations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When you open IndeeClips, the homepage shows videos selected specifically for you based on what you've watched, liked, and subscribed to. The more you use IndeeClips, the smarter these recommendations become.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>How it works:</strong> Our algorithm tracks which videos you watch all the way through, which you skip, and which topics you engage with</li>
          <li><strong>Improving recommendations:</strong> Watch videos fully if you enjoy them, click "Not Interested" on topics you don't want to see</li>
          <li><strong>Fresh vs familiar:</strong> The feed balances content from creators you know with new discoveries based on your interests</li>
          <li><strong>Refresh anytime:</strong> Pull down to refresh the homepage and load new recommendations</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          2. Search: Find Exactly What You Want
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The search bar at the top of every page is your gateway to finding specific content, creators, or topics.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Search Tips That Work
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Be specific:</strong> "Budget smartphone under 10000" finds better results than just "smartphone"</li>
          <li><strong>Use filters:</strong> After searching, filter by upload date (this week, this month) or video type (full videos vs clips)</li>
          <li><strong>Search in Hindi:</strong> IndeeClips supports Hindi search—type "खाना बनाने की विधि" to find cooking videos</li>
          <li><strong>Try variations:</strong> If you don't find what you want, try different keywords—"coding tutorial" vs "programming guide"</li>
          <li><strong>Search suggestions:</strong> As you type, IndeeClips suggests popular searches—these are real queries from other users</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          3. Categories: Browse by Topic
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips organizes content into categories to help you explore topics you love:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Comedy:</strong> Stand-up, sketches, pranks, and funny vlogs</li>
          <li><strong>Gaming:</strong> Gameplay, reviews, walkthroughs, and esports</li>
          <li><strong>Music:</strong> Music videos, covers, original songs, and concerts</li>
          <li><strong>Education:</strong> Tutorials, courses, explainers, and skill-building content</li>
          <li><strong>Entertainment:</strong> Movies, shows, celebrity interviews, and gossip</li>
          <li><strong>Technology:</strong> Gadget reviews, coding tutorials, tech news</li>
          <li><strong>Spiritual/Devotional:</strong> Bhajans, spiritual talks, meditation guides</li>
          <li><strong>And more:</strong> Fitness, cooking, fashion, travel, and niche topics</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Browse categories from the menu to discover trending videos in each topic area.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          4. Clips Feed: Endless Short-Form Entertainment
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The Clips section is your TikTok-style feed of short vertical videos (15-90 seconds). It's designed for quick entertainment and discovery.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>How to access:</strong> Tap the "Clips" icon in the bottom navigation (mobile) or top menu (desktop)</li>
          <li><strong>Swipe to explore:</strong> Swipe up to see the next clip—the algorithm learns what you like and shows more of it</li>
          <li><strong>Interact to refine:</strong> Like clips you enjoy, skip ones you don't—the feed adapts in real-time</li>
          <li><strong>Follow from clips:</strong> Found a creator you like? Tap their profile picture to see more of their content</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          5. Suggested Videos: Discover While You Watch
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When watching a video, the sidebar (desktop) or below the video (mobile) shows related content. This is one of the best ways to go down rabbit holes and find new creators.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Related videos:</strong> Content similar to what you're watching—same topic, same creator, or related channels</li>
          <li><strong>Up next:</strong> What autoplays when the current video ends (you can turn autoplay off in settings)</li>
          <li><strong>More from this channel:</strong> Explore the creator's other videos without leaving the player</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          6. Channel Pages: Deep Dive into Creators
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When you find a creator you like, visit their channel page to see all their content organized:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Recent uploads:</strong> See their latest videos first</li>
          <li><strong>Popular videos:</strong> Sort by most-viewed to find their best content</li>
          <li><strong>Playlists:</strong> Many creators organize videos into series or topics—great for binge-watching</li>
          <li><strong>About section:</strong> Learn about the creator, their upload schedule, and contact info</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          7. Subscriptions Feed: Never Miss Your Favorites
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Once you subscribe to channels, the Subscriptions page becomes your personalized feed of new uploads from creators you follow.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Chronological feed:</strong> See uploads in the order they were posted</li>
          <li><strong>Notification bell:</strong> Click the bell icon on a channel to get notified when they upload</li>
          <li><strong>Manage subscriptions:</strong> Visit the Subscriptions page to see all channels you follow and unsubscribe from inactive ones</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          8. Watch History: Revisit What You Loved
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Can't remember that video you watched last week? Your watch history saves everything you've viewed so you can find it again.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Access from menu:</strong> Find Watch History in the More menu (mobile) or sidebar (desktop)</li>
          <li><strong>Search your history:</strong> Use the search bar to find specific videos you've watched</li>
          <li><strong>Clear selectively:</strong> Remove individual videos or clear your entire history</li>
          <li><strong>Pause history:</strong> Turn off watch history temporarily if you don't want certain videos tracked</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          9. Social Discovery: Follow Recommendations from Friends
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Some of the best content comes from personal recommendations:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Share links:</strong> When friends share IndeeClips links via WhatsApp, Instagram, or SMS, tap to watch</li>
          <li><strong>Trending on social:</strong> Check what's trending on Twitter/X or Instagram—often includes viral IndeeClips videos</li>
          <li><strong>Community posts:</strong> Some creators share recommendations in their community tab</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Pro Tips for Better Discovery
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Train your algorithm:</strong> The first 20-30 videos you watch heavily influence recommendations—choose wisely</li>
          <li><strong>Use incognito mode:</strong> Want to explore without affecting recommendations? Browse in incognito/private mode</li>
          <li><strong>Clear search history:</strong> If your recommendations feel stale, clear your watch and search history to reset the algorithm</li>
          <li><strong>Explore beyond your bubble:</strong> Occasionally search for topics outside your usual interests—you might discover new passions</li>
          <li><strong>Subscribe strategically:</strong> Only subscribe to channels you actually want to watch regularly—too many subscriptions clutter your feed</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Start Exploring Today
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            The beauty of IndeeClips is that there's always something new to discover. Use these tools to find content that educates, entertains, and inspires you. Whether you're searching for specific tutorials or just browsing for entertainment, the perfect video is waiting.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Need help finding specific content or have questions about using IndeeClips? Contact us at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default DiscoverContent;
